import { useCallback, useEffect, useRef } from 'react';
import { useActivateBlendLoanMutation } from 'src/services/graphql/generated/graphql';
import { MaybeLoanID } from 'src/common/types';
import { DatadogRUMService } from 'src/services/datadog/DatadogRUMService';
import { createDatadogError } from 'src/services/datadog/utils/createDatadogError';
import { useMfaVerification } from 'src/services/auth/session/hooks';
import { useBasicToast } from '../useBasicToast';
import { createActiveBlendLoanPayload } from './utils/createActiveBlendLoanPayload';

export const StartBlendError = createDatadogError('StartBlend');

export const useStartBlend = (loanID: MaybeLoanID, borrower_id: string | null) => {
    const [getActiveBlendLoan, { loading: isLoadingBlendData }] = useActivateBlendLoanMutation();
    const sendToast = useBasicToast();
    const { hasMfaAccess, requestMfaAccess } = useMfaVerification();
    const pendingBlendRedirect = useRef(false);

    const redirectToBlend = useCallback(async () => {
        if (loanID) {
            DatadogRUMService.Client.getInstance().addAction(DatadogRUMService.CustomAction.DASHBOARD_BLEND_CTA_CLICK, {
                borrower_id,
            });
            const { data, errors } = await getActiveBlendLoan(createActiveBlendLoanPayload(loanID));
            const { activateBlendLoan } = data ?? {};
            const { success = false, blend_activation_url } = activateBlendLoan ?? {};
            if ((errors ?? []).length || !success) {
                await DatadogRUMService.Client.getInstance().addError(
                    new StartBlendError(`ActivateBlendLoan mutation failed`),
                    {
                        error: errors,
                        errorAsString: JSON.stringify(errors, null, 2),
                        data: {
                            blend_activation_url,
                            success,
                            loanID,
                        },
                    }
                );
                sendToast('We were unable to access your application. Please call your LO.', 'critical');
                return;
            }
            globalThis.location.assign(blend_activation_url!);
        }
    }, [getActiveBlendLoan, loanID, sendToast, borrower_id]);

    // Check if user just completed MFA and has pending Blend redirect
    useEffect(() => {
        if (hasMfaAccess && pendingBlendRedirect.current) {
            pendingBlendRedirect.current = false;
            redirectToBlend();
        }
    }, [hasMfaAccess, redirectToBlend]);

    const sendToBlend = useCallback(async () => {
        if (!hasMfaAccess) {
            pendingBlendRedirect.current = true;
            requestMfaAccess();
            return;
        }

        await redirectToBlend();
    }, [hasMfaAccess, requestMfaAccess, redirectToBlend]);

    return {
        sendToBlend,
        isLoadingBlendData,
    };
};
